import React, { createContext, useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import apiService from '../services/apiService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    // Carregar token dos cookies ao iniciar e buscar dados atualizados do servidor
    useEffect(() => {
        const loadUserData = async () => {
            const storedToken = Cookies.get('token');
            const storedUser = Cookies.get('user');

            if (storedToken && storedUser) {
                setToken(storedToken);
                try {
                    // Tentar buscar dados atualizados do servidor
                    const response = await apiService.getCurrentUser();
                    if (response.user) {
                        const isHttps = window.location.protocol === 'https:';
                        const cookieOpts = {
                            expires: 1,
                            path: '/',
                            ...(isHttps && {
                                sameSite: 'None',
                                secure: true,
                            }),
                        };
                        setUser(response.user);
                        Cookies.set('user', JSON.stringify(response.user), cookieOpts);
                    } else {
                        // Se falhar, usar dados dos cookies
                        setUser(JSON.parse(storedUser));
                    }
                } catch (e) {
                    console.error('Erro ao buscar dados do servidor, usando cookies:', e);
                    // Se falhar, usar dados dos cookies
                    try {
                        setUser(JSON.parse(storedUser));
                    } catch (parseError) {
                        console.error('Erro ao parsear usuário dos cookies:', parseError);
                        Cookies.remove('token');
                        Cookies.remove('user');
                    }
                }
            }
            setLoading(false);
        };

        loadUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (username, password) => {
        try {
            const response = await apiService.login(username, password);

            if (response.token && response.user) {
                setToken(response.token);
                setUser(response.user);

                // Se o protocolo for HTTPS, habilita secure + SameSite=None
                const isHttps = window.location.protocol === 'https:';
                const cookieOpts = {
                    expires: 1,  // 24 horas
                    path: '/',
                    ...(isHttps && {
                        sameSite: 'None',
                        secure: true,
                    }),
                };

                Cookies.set('token', response.token, cookieOpts);
                Cookies.set('user', JSON.stringify(response.user), cookieOpts);

                return response;
            } else {
                throw new Error('Resposta inválida do servidor');
            }
        } catch (error) {
            console.error('Erro no login:', error);
            throw error;
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        Cookies.remove('token');
        Cookies.remove('user');
    };

    const isAuthenticated = () => {
        return !!token && !!user;
    };

    const getAuthHeader = () => {
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const updateUser = (newUser) => {
        setUser(newUser);
        const isHttps = window.location.protocol === 'https:';
        const cookieOpts = {
            expires: 1,
            path: '/',
            ...(isHttps && {
                sameSite: 'None',
                secure: true,
            }),
        };
        Cookies.set('user', JSON.stringify(newUser), cookieOpts);
    };

    const updateToken = (newToken) => {
        setToken(newToken);
        const isHttps = window.location.protocol === 'https:';
        const cookieOpts = {
            expires: 1,
            path: '/',
            ...(isHttps && {
                sameSite: 'None',
                secure: true,
            }),
        };
        Cookies.set('token', newToken, cookieOpts);
    };

    const value = {
        user,
        token,
        loading,
        login,
        logout,
        isAuthenticated,
        getAuthHeader,
        setUser: updateUser,
        setToken: updateToken,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};

export default AuthContext;
